import { useRef } from "react";
import { motion, useScroll, useTransform, useSpring, useMotionValue } from "framer-motion";
import { ArrowDown, Sparkle } from "lucide-react";
import { MaskLines } from "@/components/Reveal";
import { scrollToId } from "@/lib/api";

const SPECKS = [
  { left: "6%", top: "22%", d: 7, delay: 0, s: 5 },
  { left: "14%", top: "68%", d: 9, delay: 1.2, s: 4 },
  { left: "26%", top: "38%", d: 8, delay: 0.6, s: 3 },
  { left: "44%", top: "14%", d: 10, delay: 2, s: 4 },
  { left: "58%", top: "74%", d: 7, delay: 0.9, s: 3 },
  { left: "70%", top: "26%", d: 9, delay: 1.6, s: 5 },
  { left: "84%", top: "58%", d: 8, delay: 0.3, s: 4 },
  { left: "92%", top: "36%", d: 11, delay: 2.3, s: 3 },
  { left: "36%", top: "84%", d: 9, delay: 1.8, s: 4 },
  { left: "64%", top: "50%", d: 7, delay: 2.7, s: 3 },
];

const HERO_IMG =
  "https://images.pexels.com/photos/34079355/pexels-photo-34079355.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1100&w=820";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, 56]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotX = useSpring(useTransform(my, [-0.5, 0.5], [5, -5]), { stiffness: 55, damping: 14 });
  const rotY = useSpring(useTransform(mx, [-0.5, 0.5], [-7, 7]), { stiffness: 55, damping: 14 });

  const onMouseMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      onMouseMove={onMouseMove}
      className="relative min-h-screen flex items-center overflow-hidden bg-[#180407]"
    >
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(1200px 620px at 78% 30%, rgba(212,175,55,0.14), transparent 60%), radial-gradient(900px 700px at 8% 85%, rgba(74,14,23,0.85), transparent 62%)",
        }}
      />
      {SPECKS.map((s, i) => (
        <span
          key={i}
          className="gold-speck absolute rounded-full bg-[#D4AF37] blur-[2px] pointer-events-none"
          style={{
            left: s.left,
            top: s.top,
            width: s.s,
            height: s.s,
            animationDuration: `${s.d}s`,
            animationDelay: `${s.delay}s`,
          }}
        />
      ))}

      <div className="relative z-10 max-w-7xl mx-auto w-full px-5 sm:px-8 pt-32 pb-24 grid lg:grid-cols-12 gap-14 lg:gap-10 items-center">
        <motion.div style={{ y: yText, opacity: fade }} className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.15 }}
            className="flex items-center gap-3 mb-8"
          >
            <span className="h-px w-12 bg-gradient-to-r from-transparent to-[#D4AF37]" />
            <span className="text-[11px] sm:text-xs uppercase tracking-[0.38em] text-[#D4AF37] font-semibold">
              Sai Grand Celebrations · Wedding Mandap
            </span>
          </motion.div>

          <MaskLines
            as="h1"
            delay={0.3}
            className="font-serif-lux font-medium text-[17vw] sm:text-7xl xl:text-[6.1rem] leading-[0.98] text-[#FAF6F0]"
            lines={[
              "Where Forever",
              "Begins in",
              <span key="g" className="font-serif-lux italic gold-text pr-2">Grandeur</span>,
            ]}
          />

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.95 }}
            className="mt-7 max-w-xl text-[15px] sm:text-lg font-light leading-relaxed text-[#A39791]"
          >
            A majestic new wedding mandap and celebration hall — opening shortly. From the
            sacred vows to the grandest reception, every moment deserves a stage as
            unforgettable as the day itself.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.1 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-cta-book"
              onClick={() => scrollToId("book")}
              className="relative overflow-hidden sheen rounded-full bg-[#D4AF37] px-9 py-4 text-[13px] font-semibold uppercase tracking-[0.2em] text-[#2C070B] shadow-[0_0_34px_rgba(212,175,55,0.35)] hover:shadow-[0_0_50px_rgba(212,175,55,0.55)] hover:-translate-y-0.5 transition-[transform,box-shadow] duration-400"
            >
              Reserve Your Date
            </button>
            <button
              data-testid="hero-cta-gallery"
              onClick={() => scrollToId("gallery")}
              className="rounded-full border border-[#D4AF37]/50 px-9 py-4 text-[13px] uppercase tracking-[0.2em] text-[#F3E5AB] hover:border-[#D4AF37] hover:bg-[#D4AF37]/10 transition-colors duration-400"
            >
              View the Gallery
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.35 }}
            className="mt-12 flex items-center gap-3 text-[#D4AF37]"
          >
            <Sparkle size={15} />
            <span className="text-[12px] uppercase tracking-[0.28em] text-[#A39791]">
              Now taking early enquiries · Weddings · Receptions · Sangeet · Engagements
            </span>
          </motion.div>
        </motion.div>

        <motion.div
          style={{ y: yImg, perspective: 1200 }}
          className="lg:col-span-5 relative flex justify-center lg:justify-end"
        >
          <motion.div
            initial={{ opacity: 0, y: 60, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 1.3, delay: 0.55, ease: [0.22, 1, 0.36, 1] }}
            style={{ rotateX: rotX, rotateY: rotY, transformStyle: "preserve-3d" }}
            className="relative w-[78%] sm:w-[62%] lg:w-full max-w-[420px]"
          >
            <div className="arch-frame relative border border-[#D4AF37]/45 p-[6px] shadow-[0_30px_90px_rgba(0,0,0,0.6)] bg-[#2C070B]/40">
              <div className="arch-frame overflow-hidden">
                <img
                  src={HERO_IMG}
                  alt="Luxurious floral wedding mandap decorated with grand chandeliers"
                  className="w-full aspect-[3/4.1] object-cover"
                  loading="eager"
                />
              </div>
              <div className="absolute inset-0 arch-frame pointer-events-none bg-gradient-to-t from-[#180407]/70 via-transparent to-transparent" />
            </div>

            <div
              data-testid="hero-opening-badge"
              style={{ transform: "translateZ(48px)" }}
              className="glass-card absolute -left-4 sm:-left-10 bottom-12 rounded-2xl px-5 py-4 flex items-center gap-3"
            >
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#D4AF37] opacity-60" />
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#D4AF37]" />
              </span>
              <div>
                <p className="font-display text-[12px] tracking-[0.24em] text-[#F3E5AB]">OPENING SHORTLY</p>
                <p className="text-[11px] text-[#A39791] mt-1">Early enquiries now welcome</p>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>

      <motion.button
        style={{ opacity: fade }}
        data-testid="hero-scroll-cue"
        onClick={() => scrollToId("venue")}
        className="absolute bottom-7 left-1/2 -translate-x-1/2 text-[#D4AF37] flex flex-col items-center gap-2"
        aria-label="Scroll down"
      >
        <span className="text-[10px] uppercase tracking-[0.3em] text-[#A39791]">Scroll</span>
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}>
          <ArrowDown size={17} />
        </motion.span>
      </motion.button>
    </section>
  );
};

export default Hero;
